import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ClientPortalService } from '../services/client-portal.service';
import { ClientNotification } from '../Models/ClientNotification';
import moment, { Moment } from 'moment';
import { PdfPreviewDialogComponent, PdfDocType } from './pdf-preview-dialog/pdf-preview-dialog.component';

@Component({
  selector: 'app-client-portal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    MatDialogModule,
    MatPaginatorModule
  ],
  templateUrl: './client-portal.component.html',
  styleUrls: ['./client-portal.component.css']
})
export class ClientPortalComponent implements OnInit {
  clientId!: number;
  clientName = '';
  contactName = '';
  isMultiClientMode = false;
  availableClients: { id: number; name: string }[] = [];
  notifications: ClientNotification[] = [];
  filteredNotifications: ClientNotification[] = [];
  serviceTypes: string[] = [];
  private serviceTypeData: { id: number; name: string }[] = [];
  selectedServiceTypes: string[] = [];
  nameSearchValue = '';
  probateSearchValue = '';
  startDate: Moment | null = null;
  endDate: Moment | null = null;

  selectedProbateIds = new Set<string>();
  selectedDocType: string = 'notification';
  allowCombined = false;
  selectedCombineMode: 'all' | 'separate' = 'all';

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  pageSize = 50;
  pageIndex = 0;
  readonly pageSizeOptions = [50, 100, 200, 500];
  private readonly pageSizeStorageKey = 'client-portal-page-size';

  get pagedNotifications(): ClientNotification[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredNotifications.slice(start, start + this.pageSize);
  }

  get allSelected(): boolean {
    return this.filteredNotifications.length > 0 &&
           this.filteredNotifications.every(n => this.selectedProbateIds.has(String(n.probateId)));
  }
  get someSelected(): boolean { return this.selectedProbateIds.size > 0; }

  get isNameSearchActive(): boolean { return this.nameSearchValue.trim().length > 0; }
  get isProbateSearchActive(): boolean { return this.probateSearchValue.trim().length > 0; }

  private getSelectedNotifications(): ClientNotification[] {
    return this.notifications.filter(n => this.selectedProbateIds.has(String(n.probateId)));
  }

  get firstClassCsvEligible(): boolean {
    const sel = this.getSelectedNotifications();
    if (sel.length === 0) return false;
    if (sel.some(n => n.serviceTypeId !== 1)) return false;
    const dates = new Set(sel.map(n => n.clientEmailedDate ? new Date(n.clientEmailedDate).setHours(0, 0, 0, 0) : NaN));
    return dates.size === 1 && !Number.isNaN([...dates][0]);
  }

  get firstClassCsvTooltip(): string {
    const sel = this.getSelectedNotifications();
    if (sel.length === 0) return 'Select at least one notification.';
    if (sel.some(n => n.serviceTypeId !== 1)) return 'Only Named notifications can be exported as First-Class CSV.';
    const dates = new Set(sel.map(n => n.clientEmailedDate ? new Date(n.clientEmailedDate).setHours(0, 0, 0, 0) : NaN));
    if (dates.size !== 1 || Number.isNaN([...dates][0])) return 'Please choose probates with same Date Notified for CSV files';
    return 'Download First-Class CSV files as ZIP';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private portalService: ClientPortalService,
    private dialog: MatDialog
  ) {}

  openPreview(notification: ClientNotification, docType: PdfDocType): void {
    if (docType === 'grant' && !notification.hasGop) return;
    if (docType === 'will' && !notification.hasWill) return;
    if (docType === 'notification' && !notification.hasNotification) return;
    const token = this.portalService.getToken();
    if (!token) { this.logout(); return; }
    this.dialog.open(PdfPreviewDialogComponent, {
      panelClass: 'pdf-preview-panel',
      backdropClass: 'pdf-preview-backdrop',
      autoFocus: false,
      data: {
        probateId: notification.probateId,
        deceasedName: notification.deceasedName,
        docType,
        clientId: this.clientId,
        token,
        serviceTypeName: notification.serviceTypeName
      }
    });
  }

  ngOnInit() {
    const savedSize = localStorage.getItem(this.pageSizeStorageKey);
    if (savedSize) {
      const parsed = parseInt(savedSize, 10);
      if (this.pageSizeOptions.includes(parsed)) this.pageSize = parsed;
    }

    this.clientId = +this.route.snapshot.paramMap.get('clientId')!;
    if (!this.portalService.isLoggedInAs(this.clientId)) {
      this.router.navigate(['/client'], { queryParams: { returnUrl: this.router.url, clientId: this.clientId } });
      return;
    }
    const session = this.portalService.getSession()!;
    this.availableClients = session.clients;
    this.isMultiClientMode = session.multiClientMode && session.clients.length > 1;
    this.clientName = session.clients.find(c => c.id === this.clientId)?.name ?? '';
    this.contactName = session.contactName;

    const fromParam = this.route.snapshot.queryParamMap.get('AlertFrom');
    if (fromParam) {
      const parsed = moment(fromParam, 'YYYYMMDD', true);
      if (parsed.isValid()) this.startDate = parsed;
    }
    const toParam = this.route.snapshot.queryParamMap.get('AlertTo');
    if (toParam) {
      const parsed = moment(toParam, 'YYYYMMDD', true);
      if (parsed.isValid()) this.endDate = parsed;
    }

    // Load service types then resolve ServiceType URL param (which may be a numeric ID)
    this.http.get<{ id: number; name: string }[]>('/charitycentral/GetServiceTypes').subscribe({
      next: types => {
        this.serviceTypeData = types || [];
        this.serviceTypes = this.serviceTypeData.map(t => t.name);

        const serviceTypeParam = this.route.snapshot.queryParamMap.get('ServiceType');
        if (serviceTypeParam) {
          const numericId = parseInt(serviceTypeParam, 10);
          let matchedName: string | undefined;
          if (!isNaN(numericId)) {
            matchedName = this.serviceTypeData.find(t => t.id === numericId)?.name;
          } else {
            matchedName = this.serviceTypeData.find(t => t.name.toLowerCase() === serviceTypeParam.toLowerCase())?.name;
          }
          if (matchedName) this.selectedServiceTypes = [matchedName];
        }

        this.loadNotifications();
      },
      error: () => {
        this.serviceTypes = ['Named', 'Discretionary', 'Trust'];
        this.loadNotifications();
      }
    });
  }

  loadNotifications() {
    const token = this.portalService.getToken();
    if (!token) { this.logout(); return; }
    const params = new HttpParams()
      .set('clientId', this.clientId)
      .set('token', token);
    this.http.get<ClientNotification[]>(
      '/charitycentral/GetClientPortalNotifications', { params }
    ).subscribe({
      next: r => {
        this.notifications = r || [];
        this.applyAll();
      },
      error: () => this.logout()
    });
  }

  applyFilters() {
    this.applyAll();
  }

  applySearch() {
    this.applyAll();
  }

  private applyAll() {
    let result = [...this.notifications];

    if (this.selectedServiceTypes.length > 0) {
      result = result.filter(n => this.selectedServiceTypes.includes(n.serviceTypeName ?? ''));
    }

    if (this.nameSearchValue.trim()) {
      const term = this.nameSearchValue.trim().toLowerCase();
      result = result.filter(n => (n.deceasedName ?? '').toLowerCase().includes(term));
    } else if (this.probateSearchValue.trim()) {
      const term = this.probateSearchValue.trim().toLowerCase();
      result = result.filter(n => String(n.probateId ?? '').toLowerCase().includes(term));
    }

    if (this.startDate) {
      const from = this.startDate.toDate().setHours(0, 0, 0, 0);
      result = result.filter(n => n.clientEmailedDate && new Date(n.clientEmailedDate).setHours(0, 0, 0, 0) >= from);
    }
    if (this.endDate) {
      const to = this.endDate.toDate().setHours(23, 59, 59, 999);
      result = result.filter(n => n.clientEmailedDate && new Date(n.clientEmailedDate).getTime() <= to);
    }

    this.filteredNotifications = result;
    this.selectedProbateIds.clear();
    this.pageIndex = 0;
    if (this.paginator) this.paginator.firstPage();
  }

  pageChanged(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    if (this.pageSize !== event.pageSize) {
      this.pageSize = event.pageSize;
      localStorage.setItem(this.pageSizeStorageKey, String(event.pageSize));
    }
  }

  getClientInitials(): string {
    if (!this.clientName) return '?';
    const parts = this.clientName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  clearNameSearch() { this.nameSearchValue = ''; this.applyAll(); }
  clearProbateSearch() { this.probateSearchValue = ''; this.applyAll(); }
  clearDateSearch() { this.startDate = null; this.endDate = null; this.applyAll(); }

  toggleSelectAll() {
    if (this.allSelected) {
      this.filteredNotifications.forEach(n => this.selectedProbateIds.delete(String(n.probateId)));
    } else {
      this.filteredNotifications.forEach(n => this.selectedProbateIds.add(String(n.probateId)));
    }
  }

  toggleRow(probateId: string | number | undefined) {
    const id = String(probateId);
    if (this.selectedProbateIds.has(id)) this.selectedProbateIds.delete(id);
    else this.selectedProbateIds.add(id);
  }

  isSelected(probateId: string | number | undefined): boolean {
    return this.selectedProbateIds.has(String(probateId));
  }

  downloadZip() {
    const token = this.portalService.getToken();
    if (!token || !this.selectedDocType || this.selectedProbateIds.size === 0) return;

    const params = new HttpParams()
      .set('clientId', this.clientId)
      .set('token', token)
      .set('probateIds', Array.from(this.selectedProbateIds).join(','))
      .set('docType', this.selectedDocType);

    this.http.get('/charitycentral/GetClientPortalDocumentsZip', {
      params,
      responseType: 'blob',
      observe: 'response'
    }).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          alert('No documents were returned for the selected probates.');
          return;
        }
        const disposition = response.headers.get('content-disposition') || '';
        const match = /filename\*?=(?:UTF-\d+''|")?([^;"]+)"?/i.exec(disposition);
        const filename = match?.[1] ? decodeURIComponent(match[1]) : `documents_${this.selectedDocType}.zip`;
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      },
      error: async (err) => {
        let message = `Download failed (status ${err.status || 'unknown'}).`;
        if (err.error instanceof Blob) {
          try { message += ' ' + await err.error.text(); } catch { }
        } else if (typeof err.error === 'string') {
          message += ' ' + err.error;
        }
        alert(message);
      }
    });
  }

  downloadCombined() {
    const token = this.portalService.getToken();
    if (!token || this.selectedProbateIds.size === 0) return;

    const params = new HttpParams()
      .set('clientId', this.clientId)
      .set('token', token)
      .set('probateIds', Array.from(this.selectedProbateIds).join(','))
      .set('mode', this.selectedCombineMode)
      .set('serviceTypes', this.selectedServiceTypes.join(','));

    this.http.get('/charitycentral/GetClientPortalCombinedDocumentsZip', {
      params,
      responseType: 'blob',
      observe: 'response'
    }).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          alert('No documents were returned for the selected probates.');
          return;
        }
        const disposition = response.headers.get('content-disposition') || '';
        const match = /filename\*?=(?:UTF-\d+''|")?([^;"]+)"?/i.exec(disposition);
        const filename = match?.[1] ? decodeURIComponent(match[1]) : `combined_documents_${this.selectedCombineMode}.zip`;
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      },
      error: async (err) => {
        let message = `Download failed (status ${err.status || 'unknown'}).`;
        if (err.error instanceof Blob) {
          try { message += ' ' + await err.error.text(); } catch { }
        } else if (typeof err.error === 'string') {
          message += ' ' + err.error;
        }
        alert(message);
      }
    });
  }

  downloadFirstClassCsv() {
    const token = this.portalService.getToken();
    if (!token || !this.firstClassCsvEligible) return;

    const params = new HttpParams()
      .set('clientId', this.clientId)
      .set('token', token)
      .set('probateIds', Array.from(this.selectedProbateIds).join(','));

    this.http.get('/charitycentral/GetClientPortalFirstClassCsvZip', {
      params,
      responseType: 'blob',
      observe: 'response'
    }).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          alert('No CSV files were generated for the selected probates.');
          return;
        }
        const disposition = response.headers.get('content-disposition') || '';
        const match = /filename\*?=(?:UTF-\d+''|")?([^;"]+)"?/i.exec(disposition);
        const filename = match?.[1] ? decodeURIComponent(match[1]) : `${this.clientId}_firstclass.zip`;
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      },
      error: async (err) => {
        let message = `Download failed (status ${err.status || 'unknown'}).`;
        if (err.error instanceof Blob) {
          try { message += ' ' + await err.error.text(); } catch { }
        } else if (typeof err.error === 'string') {
          message += ' ' + err.error;
        }
        alert(message);
      }
    });
  }

  selectClient(clientId: number): void {
    if (clientId === this.clientId) return;
    this.clientId = clientId;
    this.clientName = this.availableClients.find(c => c.id === clientId)?.name ?? '';
    this.portalService.setSelectedClient(clientId);
    this.selectedProbateIds.clear();
    this.notifications = [];
    this.filteredNotifications = [];
    this.loadNotifications();
  }

  logout() {
    this.portalService.clearSession();
    this.router.navigate(['/client']);
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }
}
